/**
 * ZoneMapTab Component
 *
 * Tab component for map-based zone creation
 * Allows users to draw shapes on a recall map and create zones
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent } from '@vehix/portal/components/ui/card';
import { Button } from '@vehix/portal/components/ui/button';
import { Input } from '@vehix/portal/components/ui/input';
import { Label } from '@vehix/portal/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@vehix/portal/components/ui/dialog';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Checkbox } from '@vehix/portal/components/ui/checkbox';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import {
  Loader2,
  List,
  Map,
  ChevronDown,
  Filter,
  X,
  Maximize2,
  Minimize2,
  CircleDot,
  Building2,
  Paintbrush,
  Download,
  FileSpreadsheet,
  PencilRuler,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { Switch } from '@vehix/portal/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@vehix/portal/components/ui/tabs';
import { ZoneDrawingMapOptimized } from './ZoneDrawingMapOptimized';
import { useStableDealerClusteringCached } from '../hooks/useCachedRecallData';
import { useZones, useZoneMutations, useCurrentRecallCounts } from '../hooks/useZones';
import { useAllTenants } from '@vehix/portal/hooks/useTenants';
import { isInternalUseTenant } from '../utils/payingCustomers';
import { useMultiZoneZipExport } from '../hooks/useMultiZoneZipExport';
import { useMapPresentationExport } from '../hooks/useMapPresentationExport';
import { useZoneZipBoundaries } from '../hooks/useZoneZipBoundaries';
import { computeZoneColorAssignment } from '../utils/zoneAdjacencyColoring';
import { ASSIGNED_ZONE_COLOR, AVAILABLE_ZONE_COLOR } from '../utils/oemColors';
import { Zone, tenantAPI, type ZoneMetadata } from '@vehix/portal/services/api/tenants';
import {
  normalizeGeometry,
  isGeometryTooLarge,
  filterPointsInZones,
  type GeoJSONGeometry,
  type NormalizedGeometry
} from '../utils/zoneGeometry';
import { lookupZipCodes } from '../services/zipCodeLookup';
import { calculateZoneStatistics, getZoneSizeLabel } from '../utils/zoneStatistics';
import { describeRecommendedSizes, isZoneTooLarge } from '../utils/zoneSizeThresholds';
import { Popover, PopoverContent, PopoverTrigger } from '@vehix/portal/components/ui/popover';
import { cn } from '@vehix/portal/lib/utils';
import { useCampaignPriorities } from '@vehix/portal/hooks/useCampaignPriorities';
import { useAuth } from '@vehix/shared/admin/portal-bridge';
import { getExcludedDealers } from '@vehix/portal/features/recalls/services/queryBuilder';
import { supabase } from '@vehix/shared/admin/portal-bridge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import { KNOWN_OEMS, DEFAULT_OEM, getBrandsForOEM } from '@vehix/portal/constants/oems';

// Add CSS for bubbling animation
const styles = `
  @keyframes zipBubble {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
  }
  
  .zip-bubble-animation {
    animation: zipBubble 1.5s infinite;
  }
`;

interface ZoneMapTabProps {
  /** Optional view-toggle state — when provided, the header renders a List/Map segmented control. */
  view?: 'list' | 'map';
  onViewChange?: (view: 'list' | 'map') => void;
}

export const ZoneMapTab: React.FC<ZoneMapTabProps> = ({ view, onViewChange }) => {
  const { session } = useAuth();
  const { toast } = useToast();
  const { priorities: campaignPreferences } = useCampaignPriorities(session?.user?.id);

  // Use cached zone data
  const {
    data: zones = [],
    isLoading: loadingZones,
    error: loadingZonesError,
    refetch: refetchZones
  } = useZones();
  const { createZone, updateZone, deleteZone } = useZoneMutations();

  // Get count of excluded dealers and active campaign filters
  const [excludedDealerCount, setExcludedDealerCount] = useState<number>(0);
  const activeCampaignFilters = useMemo(() => {
    if (!campaignPreferences) return 0;
    return Object.keys(campaignPreferences).length;
  }, [campaignPreferences]);

  const [drawnGeometry, setDrawnGeometry] = useState<GeoJSONGeometry | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [zoneName, setZoneName] = useState('');
  const [zoneDescription, setZoneDescription] = useState('');
  const [zoneRegion, setZoneRegion] = useState('');
  const [zoneStats, setZoneStats] = useState<{
    zipCodes: string[];
    // The OEM the counts below were computed under. Carried here rather than read
    // from `selectedOem` at save time so the figure shown to the user and the `oem`
    // written to the zone row can never come from two different values — a stale
    // `handleShapeDrawn` closure once counted Ford recalls into a Toyota zone.
    oem: string;
    totalRecalls: number;
    uniqueVins: number;
    overlappingZones?: { zoneName: string; overlappingZips: string[] }[];
    isLoadingStats?: boolean;
    statsError?: boolean;
  } | null>(null);

  // True while the dialog's overlap-validation RPC is in flight. The
  // Create button stays disabled while this is true so a fast click can't
  // race past the validation result. Lets us skip the redundant RPC that
  // handleCreateZone used to fire as a safety net.
  const [isCheckingOverlaps, setIsCheckingOverlaps] = useState(false);

  const [selectedZoneId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState<Zone | null>(null);
  const [updatingZoneBoundaries, setUpdatingZoneBoundaries] = useState(false);
  const [shouldClearDrawnShapes, setShouldClearDrawnShapes] = useState(false);
  // Snapshot is always the latest now — the explicit selector was removed
  // when the floating filter overlay landed. Kept as a const so the
  // remaining call sites that pass it through to RPCs still type-check.
  const selectedSnapshotDate: string | null = null;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showBubbles, setShowBubbles] = useState(true);
  const [uniformColor, setUniformColor] = useState(false);

  // Mode toggle: 'design' = today's drawing/editing experience (drawing tools,
  // dealer bubbles, recall-tier zone colors). 'presentation' = customer-
  // shareable surface (drawing tools hidden, dealer bubbles hidden,
  // OEM-branded zone fills, centroid cards, PNG/ZIP export). Filters and
  // selection state survive mode switches; mode-specific toggles do not.
  const [mapMode, setMapMode] = useState<'design' | 'presentation'>('design');
  // Recall-count lines on the zone labels. Default off: the territory and its
  // availability are the story; counts are opt-in detail.
  const [showNowRecalls, setShowNowRecalls] = useState(false);
  const [showCreationRecalls, setShowCreationRecalls] = useState(false);
  // Tenant names on assigned zones. Default on for the admin's own view; turned
  // off before showing a dealer, so competitors' names stay off screen and PNG.
  // Not reset on mode switch — a round trip through Design must not quietly
  // bring hidden names back.
  const [showTenantNames, setShowTenantNames] = useState(true);
  // Recall-density layer (smaller, OEM-colored dealer bubbles in Presentation
  // mode). Default off so the customer sees a clean territory map first; the
  // admin flips this on when telling the "where is the recall pressure" story.
  const [showRecallDensity, setShowRecallDensity] = useState(false);

  // Zone and Region filters
  const [zoneFilter, setZoneFilter] = useState<string[]>([]);
  const [regionFilter, setRegionFilter] = useState<string>('all');
  // availableRegions is derived from the cached zones list below — see the
  // useMemo a few hundred lines down. Removed the dedicated state +
  // tenantAPI.getUniqueRegions() RPC (which fired the same heavy
  // `get_all_zones_with_counts` query that useZones already caches),
  // saving ~2 redundant primary-DB roundtrips per Zone Map session.

  // OEM filter for zones - determines which OEM's zones to show on map and create zones for
  const [selectedOem, setSelectedOem] = useState<string>(DEFAULT_OEM);

  // Reset zone multi-select when OEM or region changes (selected IDs may no longer be valid)
  useEffect(() => {
    setZoneFilter([]);
  }, [selectedOem, regionFilter]);

  // Hook must be called after state declarations
  const {
    dealerLocations,
    loading: recallsLoading,
    error: recallsError,
    loadViewportData
  } = useStableDealerClusteringCached(session?.user?.id, selectedSnapshotDate, selectedOem);

  const [currentViewportBounds, setCurrentViewportBounds] = useState<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);
  const previousBoundsRef = useRef<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);
  const lastLoadedBoundsRef = useRef<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);

  // Helper function to calculate viewport center
  const getViewportCenter = (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => {
    return {
      lat: (bounds.north + bounds.south) / 2,
      lng: (bounds.east + bounds.west) / 2
    };
  };

  // Helper function to check if viewport has moved significantly
  // Returns true if center moved more than threshold or bounds don't overlap much
  const hasViewportMovedSignificantly = (
    prev: { north: number; south: number; east: number; west: number } | null,
    current: { north: number; south: number; east: number; west: number }
  ): boolean => {
    if (!prev) return true; // First load

    const prevCenter = getViewportCenter(prev);
    const currentCenter = getViewportCenter(current);

    // Check if center moved more than ~2 degrees (significant geographic move)
    const centerDistance = Math.sqrt(
      Math.pow(currentCenter.lat - prevCenter.lat, 2) +
        Math.pow(currentCenter.lng - prevCenter.lng, 2)
    );

    if (centerDistance > 2.0) {
      return true; // Significant move (e.g., Florida to Detroit)
    }

    // Check if bounds overlap significantly
    // Calculate overlap percentage
    const overlapLat = Math.max(
      0,
      Math.min(prev.north, current.north) - Math.max(prev.south, current.south)
    );
    const overlapLng = Math.max(
      0,
      Math.min(prev.east, current.east) - Math.max(prev.west, current.west)
    );

    const prevHeight = prev.north - prev.south;
    const prevWidth = prev.east - prev.west;
    const currentHeight = current.north - current.south;
    const currentWidth = current.east - current.west;

    const overlapArea = overlapLat * overlapLng;
    const prevArea = prevHeight * prevWidth;
    const currentArea = currentHeight * currentWidth;
    const minArea = Math.min(prevArea, currentArea);

    // If overlap is less than 30% of the smaller area, it's a significant move
    const overlapRatio = minArea > 0 ? overlapArea / minArea : 0;

    return overlapRatio < 0.3;
  };

  // Memoize the bounds change handler
  // Load data on significant viewport changes (e.g., moving to different region)
  // But not on every small pan/zoom (which causes cluster breakdown)
  const handleMapBoundsChange = useCallback(
    (bounds: { north: number; south: number; east: number; west: number }) => {
      const prev = previousBoundsRef.current;

      // Bail out before touching state when the bounds are effectively unchanged.
      //
      // This used to run after setCurrentViewportBounds, which made every re-fire — a
      // programmatic recentre, a zoom that lands back where it started — produce a new
      // bounds object, a new zonesInViewport array, and therefore a new query key for the
      // recall-count RPC. That RPC takes ~24s for a large zone set, so a re-key that
      // changes nothing visible is expensive. The source events are moveend/zoomend, one
      // per completed gesture, so there is no stream to debounce: suppressing the no-op
      // re-fires is the whole win available here.
      if (
        prev &&
        Math.abs(prev.north - bounds.north) < 0.0001 &&
        Math.abs(prev.south - bounds.south) < 0.0001 &&
        Math.abs(prev.east - bounds.east) < 0.0001 &&
        Math.abs(prev.west - bounds.west) < 0.0001
      ) {
        return; // Bounds haven't changed - skip
      }

      // Update viewport bounds for zone filtering (client-side)
      previousBoundsRef.current = bounds;
      setCurrentViewportBounds(bounds);

      // Check if viewport has moved significantly (e.g., Florida to Detroit)
      // Only reload data on significant moves, not on every pan/zoom
      const lastLoaded = lastLoadedBoundsRef.current;
      // If no data has been loaded yet, always load (initial load case)
      if (!lastLoaded || hasViewportMovedSignificantly(lastLoaded, bounds)) {
        lastLoadedBoundsRef.current = bounds;
        loadViewportData(bounds);
      }
    },
    [loadViewportData]
  );

  // Filter dealers by OEM - only show dealers with vehicles from selected OEM's brands
  const filteredDealerLocations = useMemo(() => {
    if (!selectedOem) return dealerLocations;

    const oemBrands = getBrandsForOEM(selectedOem);
    if (oemBrands.length === 0) return dealerLocations; // No mapping = show all

    return dealerLocations.filter(dealer => {
      // Check if dealer has any vehicles matching OEM brands
      return dealer.makes.some(make =>
        oemBrands.some(brand => make.toLowerCase() === brand.toLowerCase())
      );
    });
  }, [dealerLocations, selectedOem]);

  // Calculate total dealers and recalls for header (using filtered data)
  const totalDealers = filteredDealerLocations.length;
  const totalRecalls = filteredDealerLocations.reduce(
    (sum, dealer) => sum + (dealer.recallCount || 0),
    0
  );

  // Filter zones by viewport bounds, zone name filter, region filter, and OEM
  const zonesInViewport = useMemo(() => {
    // Map view shows ACTIVE zones only — inactive ones don't belong on a
    // customer-shareable surface. This filter used to live in useZones() but
    // moved here so the Zone List page can consume the same hook to get the
    // full set including inactive zones.
    let filtered = zones.filter(zone => zone.status === 'active');

    // Apply OEM filter - only show zones for the selected OEM
    if (selectedOem) {
      filtered = filtered.filter(zone => zone.oem === selectedOem);
    }

    // Apply zone name filter (multi-select: empty array = all zones).
    // In Presentation mode we keep all OEM/region-filtered zones in the
    // render list so non-selected zones can show as faded context; the
    // zoneFilter is forwarded to the map's `presentationMode.selectedZoneIds`
    // and the polygon style decides selected-vs-faded.
    if (mapMode === 'design' && zoneFilter.length > 0) {
      filtered = filtered.filter(zone => zoneFilter.includes(zone.id));
    }

    // Apply region filter
    if (regionFilter && regionFilter !== 'all') {
      filtered = filtered.filter(zone => zone.region === regionFilter);
    }

    // Apply viewport bounds filter
    if (currentViewportBounds) {
      filtered = filtered.filter(zone => {
        const geometry = (zone.metadata as any)?.geometry;
        if (!geometry || !geometry.coordinates) return false;

        // Check if zone geometry intersects with viewport bounds using bounding box
        try {
          // Get bounding box of zone geometry
          const coords = geometry.coordinates;
          let minLat = Infinity;
          let maxLat = -Infinity;
          let minLng = Infinity;
          let maxLng = -Infinity;

          const extractBounds = (coords: any[]): void => {
            if (Array.isArray(coords[0])) {
              coords.forEach(coord => extractBounds(coord));
            } else if (
              coords.length >= 2 &&
              typeof coords[0] === 'number' &&
              typeof coords[1] === 'number'
            ) {
              const [lng, lat] = coords;
              minLat = Math.min(minLat, lat);
              maxLat = Math.max(maxLat, lat);
              minLng = Math.min(minLng, lng);
              maxLng = Math.max(maxLng, lng);
            }
          };

          extractBounds(coords);

          // Check if bounding boxes intersect (if any part of zone is visible in viewport)
          return !(
            maxLat < currentViewportBounds.south ||
            minLat > currentViewportBounds.north ||
            maxLng < currentViewportBounds.west ||
            minLng > currentViewportBounds.east
          );
        } catch (error) {
          // If geometry parsing fails, include the zone to be safe
          return true;
        }
      });
    }

    return filtered;
  }, [zones, currentViewportBounds, zoneFilter, regionFilter, selectedOem, mapMode]);

  // Zones to drive the centroid cards, PNG legend, and ZIP export. In
  // Presentation mode the user's explicit selection wins; with no selection
  // we fall back to "everything currently rendered" so single-zone exports
  // and zoom-and-export flows work without forcing checkbox interaction.
  const zonesForExport = useMemo(() => {
    if (mapMode !== 'presentation') return [] as Zone[];
    if (zoneFilter.length === 0) return zonesInViewport;
    const selected = new Set(zoneFilter);
    return zonesInViewport.filter(z => selected.has(z.id));
  }, [mapMode, zoneFilter, zonesInViewport]);

  // zone_id → tenant name(s), driving the available/assigned styling in
  // Presentation mode. Built by inverting the cached all-tenants list (the same
  // pattern as Tenant Setup's zoneOwners): it carries tenant_type, which the
  // zone→name mapping RPC doesn't, and keeps every owner of a zone rather than
  // the last one. Internal and demo tenants are skipped — their zones back
  // proposals, so they still read as available. Only fetched in Presentation.
  const { data: allTenants } = useAllTenants({ enabled: mapMode === 'presentation' });
  const tenantByZone = useMemo(() => {
    if (!allTenants) return undefined;
    const owners: Record<string, string[]> = {};
    allTenants
      .filter(tenant => !isInternalUseTenant(tenant))
      .forEach(tenant => {
        (tenant.zones ?? []).forEach(zone => {
          (owners[zone.id] ??= []).push(tenant.tenant_name);
        });
      });
    return Object.fromEntries(
      Object.entries(owners).map(([zoneId, names]) => [zoneId, names.join(', ')])
    );
  }, [allTenants]);

  // Current ("now") recall counts indexed by zone id, used both by the
  // centroid cards and the PNG legend strip. The hook is set-canonicalized
  // so identical zone-id sets share one cache entry across re-renders.
  //
  // No tenant id is passed: the server scopes each zone's count to the tenant it is
  // assigned to, which is what makes these numbers match the dealer's recall map.
  //
  // Gated on bounds existing. `zonesInViewport` skips the bounding-box filter entirely
  // while `currentViewportBounds` is null, so without this guard the id list is every
  // active zone of the selected OEM. That is harmless on a cold load (zones are still
  // empty, so the hook stays disabled) but not on a remount with a warm cache — toggling
  // map/list, or revisiting within useZones' 10-minute staleTime — where zones are already
  // populated on the first render and the initial-bounds effect below has not run yet.
  // The hook disables itself on an empty id list, so [] is the off switch.
  const recallCountsZoneIds = useMemo(
    () => (currentViewportBounds ? zonesInViewport.map(z => z.id) : []),
    [zonesInViewport, currentViewportBounds]
  );
  const { data: recallData } = useCurrentRecallCounts(recallCountsZoneIds);
  const recallCountsNow = recallData?.counts;

  // The crawl window behind each count, surfaced in the Design-mode zone popup so a
  // zone partly built on an older crawl is distinguishable from a freshly crawled one.
  const crawlDatesNow = useMemo(() => {
    const latest = recallData?.snapshotDates;
    const oldest = recallData?.oldestSnapshotDates;
    if (!latest && !oldest) return undefined;
    const ids = new Set([...Object.keys(latest ?? {}), ...Object.keys(oldest ?? {})]);
    const merged: Record<string, { latest?: string; oldest?: string }> = {};
    ids.forEach(id => {
      merged[id] = {
        ...(latest?.[id] && { latest: latest[id] }),
        ...(oldest?.[id] && { oldest: oldest[id] })
      };
    });
    return merged;
  }, [recallData?.snapshotDates, recallData?.oldestSnapshotDates]);

  // Per-zone ZIP boundary GeoJSON, used to render thin gray subdivision lines
  // inside each zone polygon in Presentation mode. Only fetched while the
  // page is in Presentation mode — Design mode never pays for the boundary
  // fetch. The hook canonicalizes its zone-id input so the cache is shared
  // across renders the same way useCurrentRecallCounts is.
  const { data: zipBoundariesData } = useZoneZipBoundaries(zonesForExport, {
    enabled: mapMode === 'presentation' && zonesForExport.length > 0
  });
  const zipBoundariesByZone = zipBoundariesData?.byZone;

  // Deep navy bubble color for the recall-density layer. Maximum lightness
  // contrast against any orange shade (~12:1 WCAG ratio), professional
  // data-viz feel — what Mapbox/Tableau/Carto use for "data points on a
  // thematic map." Brand-uniform across OEMs.
  const presentationBubbleColor = useMemo(() => ({ fill: '#0f172a', stroke: '#020617' }), []);

  // Four-color graph coloring for visible zones. Adjacent zones GUARANTEED
  // get different palette indexes; the Map<zoneId, paletteIndex> is then
  // looked up in VEHIX_ZONE_PALETTE inside ZoneDrawingMapOptimized.
  //
  // CRITICAL: this memo MUST be keyed off a stable string hash of the
  // sorted visible zone-id set, NOT off `zonesInViewport` (which is itself
  // a memoized array whose reference changes on every viewport pan/zoom
  // because its dep `currentViewportBounds` updates on every map move).
  // Without the hash trick, the coloring runs on every pan and freezes
  // the browser. With it, coloring runs only when the actual zone *set*
  // changes (filter changes, zoom changes that bring zones in/out of view).
  const zoneIdSetHash = useMemo(
    () =>
      mapMode === 'presentation'
        ? zonesInViewport
            .map(z => z.id)
            .sort()
            .join('|')
        : '',
    [mapMode, zonesInViewport]
  );
  // Cache zones by id so the coloring memo can rebuild its input without
  // touching the unstable `zonesInViewport` reference.
  // Using `globalThis.Map` because the file imports `Map` from lucide-react
  // for the toolbar icon — that import shadows the global Map constructor.
  const zonesByIdRef = useRef<globalThis.Map<string, Zone>>(new globalThis.Map());
  zonesByIdRef.current = useMemo(() => {
    const m = new globalThis.Map<string, Zone>();
    zonesInViewport.forEach(z => m.set(z.id, z));
    return m;
  }, [zonesInViewport]);

  const zoneColorAssignment = useMemo(() => {
    if (mapMode !== 'presentation' || !zoneIdSetHash) return undefined;
    const ids = zoneIdSetHash.split('|').filter(Boolean);
    const inputs: { id: string; geometry: never }[] = [];
    ids.forEach(id => {
      const zone = zonesByIdRef.current.get(id);
      if (!zone) return;
      const geometry = (zone.metadata as Record<string, unknown> | undefined)?.['geometry'];
      if (!geometry) return;
      inputs.push({ id, geometry: geometry as never });
    });
    return computeZoneColorAssignment(inputs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapMode, zoneIdSetHash]);

  const presentationConfig = useMemo(() => {
    if (mapMode !== 'presentation') return undefined;
    return {
      // Centroid markers are off — replaced by the side-column label overlay
      // with leader lines (see ZoneLabelOverlay). Centroid cards collided in
      // dense zone clusters; the side layout is collision-free by design.
      showCentroidCards: false,
      showNowRecalls,
      showCreationRecalls,
      showRecallDensity,
      ...(recallCountsNow && { recallCountsNow }),
      selectedZoneIds: zoneFilter,
      bubbleColor: presentationBubbleColor,
      ...(zipBoundariesByZone && { zipBoundariesByZone }),
      ...(zoneColorAssignment && { zoneColorAssignment }),
      ...(tenantByZone && { tenantByZone }),
      showTenantNames
    };
  }, [
    mapMode,
    showNowRecalls,
    showCreationRecalls,
    showRecallDensity,
    recallCountsNow,
    zoneFilter,
    presentationBubbleColor,
    zipBoundariesByZone,
    zoneColorAssignment,
    tenantByZone,
    showTenantNames
  ]);

  // Recall-density dots, limited to dealers inside the zones being presented
  // (the Zones selection, or every visible zone when nothing is selected).
  // Keyed on the zone-id set rather than `zonesForExport`, whose reference
  // changes on every pan — re-filtering would also re-create every dot marker.
  const densityZoneIdsHash = useMemo(
    () =>
      zonesForExport
        .map(z => z.id)
        .sort()
        .join('|'),
    [zonesForExport]
  );
  const densityDealerLocations = useMemo(() => {
    if (!showRecallDensity) return filteredDealerLocations;
    const geometries = densityZoneIdsHash
      .split('|')
      .filter(Boolean)
      .map(id => {
        const metadata = zonesByIdRef.current.get(id)?.metadata as
          | Record<string, unknown>
          | undefined;
        return metadata?.['geometry'] as NormalizedGeometry | undefined;
      });
    return filterPointsInZones(filteredDealerLocations, geometries);
  }, [showRecallDensity, filteredDealerLocations, densityZoneIdsHash]);

  // Reset display toggles when switching modes so each mode boots into a
  // sensible default. Design's default is showBubbles=true; Presentation's
  // defaults are recall-line toggles OFF and showRecallDensity OFF (clean
  // shareable map first; admin opts into counts or the density layer when
  // telling that story).
  useEffect(() => {
    setShowNowRecalls(false);
    setShowCreationRecalls(false);
    setShowRecallDensity(false);
    if (mapMode === 'presentation') {
      setShowBubbles(false);
    }
  }, [mapMode]);

  // PNG and ZIP export hooks
  const { exportPng, isExporting: isExportingPng } = useMapPresentationExport();
  const { exportZips, isExportingZips } = useMultiZoneZipExport();

  const handleDownloadPng = useCallback(() => {
    void exportPng({
      zones: zonesForExport,
      ...(recallCountsNow && { recallCountsNow }),
      ...(crawlDatesNow && { crawlDatesNow }),
      includeNowRecalls: showNowRecalls,
      includeCreationRecalls: showCreationRecalls,
      includeRecallDensity: showRecallDensity,
      ...(tenantByZone && { tenantByZone }),
      includeTenantNames: showTenantNames,
      ...(session?.user?.email && { attribution: session.user.email })
    });
  }, [
    exportPng,
    zonesForExport,
    recallCountsNow,
    crawlDatesNow,
    showNowRecalls,
    showCreationRecalls,
    showRecallDensity,
    tenantByZone,
    showTenantNames,
    session?.user?.email
  ]);

  const handleDownloadZips = useCallback(() => {
    void exportZips(zonesForExport);
  }, [exportZips, zonesForExport]);

  // Invalidate map size when going fullscreen to fix viewport
  useEffect(() => {
    if (isFullscreen) {
      // Small delay to ensure DOM has updated
      const timer = setTimeout(() => {
        // Trigger window resize event to let Leaflet know to recalculate
        window.dispatchEvent(new Event('resize'));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isFullscreen]);

  // Esc key exits fullscreen — standard expectation across fullscreen UIs.
  // Without this, users have no obvious keyboard escape if they can't find
  // the on-screen exit button.
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen]);

  // Zones are now loaded via useZones hook with caching

  useEffect(() => {
    // Load initial data for Florida
    const initialBounds = {
      north: 31.0,
      south: 24.5,
      east: -79.5,
      west: -87.6
    };
    setCurrentViewportBounds(initialBounds);
    previousBoundsRef.current = initialBounds;
    lastLoadedBoundsRef.current = initialBounds;
    loadViewportData(initialBounds);
  }, [loadViewportData]);

  // Get excluded dealer count for filter display
  useEffect(() => {
    if (session?.user?.id) {
      getExcludedDealers(supabase, session.user.id)
        .then(dealers => {
          setExcludedDealerCount(dealers.length);
        })
        .catch(() => {
          // Silently handle error
        });
    }
  }, [session?.user?.id]);

  // Available regions are derived from the cached `zones` array via useMemo
  // below — no dedicated RPC is needed since useZones already loads + caches
  // the underlying zone list.

  const availableRegions = useMemo(() => {
    const regionSet = new Set<string>();
    zones.forEach(z => {
      if (z.region) regionSet.add(z.region);
    });
    return Array.from(regionSet).sort();
  }, [zones]);

  // Check for ZIP code overlaps with existing zones using server-side validation
  // This is async because it uses an RPC call to the database
  const checkZipCodeOverlapsAsync = useCallback(
    async (
      zipCodesToCheck: string[],
      excludeZoneId?: string
    ): Promise<{ zoneName: string; overlappingZips: string[] }[]> => {
      if (!zipCodesToCheck || zipCodesToCheck.length === 0) {
        console.log('[ZoneMapTab] checkZipCodeOverlapsAsync: No ZIP codes to check');
        return [];
      }

      try {
        console.log('[ZoneMapTab] checkZipCodeOverlapsAsync: Calling server-side validation with', {
          zipCodesToCheck: zipCodesToCheck.length,
          zipCodesSample: zipCodesToCheck.slice(0, 10),
          excludeZoneId,
          oem: selectedOem
        });

        // Pass selectedOem to only check overlaps within the same OEM
        const validation = await tenantAPI.validateZipCodeOverlaps(
          zipCodesToCheck,
          excludeZoneId,
          selectedOem
        );

        console.log('[ZoneMapTab] checkZipCodeOverlapsAsync result:', {
          hasOverlaps: validation.hasOverlaps,
          conflicts: validation.conflicts,
          conflictingZips: validation.conflicts.flatMap(c => c.overlappingZips)
        });

        if (validation.hasOverlaps) {
          return validation.conflicts.map(conflict => ({
            zoneName: conflict.zoneName,
            overlappingZips: conflict.overlappingZips
          }));
        }

        return [];
      } catch (error) {
        console.error('[ZoneMapTab] checkZipCodeOverlapsAsync error:', error);
        return [];
      }
    },
    [selectedOem]
  );

  // Synchronous version for backward compatibility (always returns empty, use async version)
  const checkZipCodeOverlaps = useCallback(
    (
      zipCodesToCheck: string[],
      excludeZoneId?: string
    ): { zoneName: string; overlappingZips: string[] }[] => {
      // This is a placeholder - actual overlap checking is done async via checkZipCodeOverlapsAsync
      // Return empty array to not block, the async version will update state
      console.log(
        '[ZoneMapTab] checkZipCodeOverlaps (sync): Returning empty, use async version for actual check'
      );
      return [];
    },
    []
  );

  // Re-check overlaps when dialog opens or ZIP codes change. This is the
  // SINGLE source of truth for overlap validation — handleCreateZone reads
  // zoneStats.overlappingZones from state instead of firing the RPC again.
  // The Create button is disabled while isCheckingOverlaps is true so a
  // fast click can't race past the result.
  useEffect(() => {
    if (!zoneStats?.zipCodes || zoneStats.zipCodes.length === 0 || !dialogOpen) {
      return;
    }
    let cancelled = false;
    setIsCheckingOverlaps(true);
    checkZipCodeOverlapsAsync(zoneStats.zipCodes)
      .then(overlappingZones => {
        if (cancelled) return;
        setZoneStats(prev => (prev ? { ...prev, overlappingZones } : prev));
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[ZoneMapTab] Overlap check failed:', err);
        // Leave existing overlaps untouched on transient failure; the user
        // can still try to submit and the server will reject if needed.
      })
      .finally(() => {
        if (cancelled) return;
        setIsCheckingOverlaps(false);
      });
    return () => {
      cancelled = true;
    };
  }, [zoneStats?.zipCodes, dialogOpen, checkZipCodeOverlapsAsync]);

  // Handle shape drawn on map
  const handleShapeDrawn = useCallback(
    async (geometry: GeoJSON.Geometry) => {
      try {
        // Normalize geometry (handles LineString → Polygon buffering, cleaning, simplification)
        const normalized = normalizeGeometry(geometry as GeoJSONGeometry);

        // Warn if geometry is too large
        if (isGeometryTooLarge(normalized)) {
          toast({
            title: 'Large Zone Warning',
            description: 'This zone is very large (>1000 sq km). Processing may take longer.',
            variant: 'default'
          });
        }

        setDrawnGeometry(normalized);

        // Read the OEM once and thread that single value through the count, the
        // dialog copy and the saved zone row. Everything downstream reads
        // zoneStats.oem, never selectedOem, so the three can't drift apart.
        const oemForStats = selectedOem;

        // Lookup ZIP codes
        setCreating(true);
        const zipCodes = await lookupZipCodes(normalized);

        if (zipCodes.length === 0) {
          toast({
            title: 'No ZIP Codes Found',
            description:
              'No ZIP codes were found within the drawn shape. Please try a different area.',
            variant: 'default'
          });
          setCreating(false);
          return;
        }

        // Open dialog immediately with ZIP codes (before checking overlaps)
        // This prevents the dialog from appearing frozen while stats/overlaps are calculated
        const initialZoneStats = {
          zipCodes,
          oem: oemForStats,
          totalRecalls: 0, // Will be updated when stats are ready
          uniqueVins: 0, // Will be updated when stats are ready
          overlappingZones: [], // Will be updated by useEffect when async check completes
          isLoadingStats: true // Flag to show loading state
        };

        console.log('[ZoneMapTab] Setting initial zoneStats:', {
          zipCodes: initialZoneStats.zipCodes.length,
          oem: oemForStats
        });

        setZoneStats(initialZoneStats);

        // Always open dialog immediately - overlaps will be checked async by useEffect
        console.log('[ZoneMapTab] Opening zone creation dialog:', {
          zipCodesCount: zipCodes.length,
          zipCodesSample: zipCodes.slice(0, 10)
        });
        setDialogOpen(true);
        setCreating(false); // Allow user to interact with dialog while stats load

        // Calculate zone statistics from database with proper filtering (async, non-blocking)
        // This ensures we get ALL recalls for the zone, not just viewport data
        // Pass oemForStats so the count is scoped to the OEM the zone will be saved under
        calculateZoneStatistics(
          zipCodes,
          session?.user?.id,
          campaignPreferences,
          selectedSnapshotDate,
          oemForStats
        )
          .then(stats => {
            // Update stats when ready - preserve overlappingZones
            setZoneStats(prev => {
              if (!prev) {
                console.error('[ZoneMapTab] prev is null when updating stats!');
                return prev;
              }

              const updated = {
                ...prev,
                totalRecalls: stats.totalRecalls,
                uniqueVins: stats.uniqueVins,
                isLoadingStats: false,
                // Explicitly preserve overlappingZones
                overlappingZones: prev.overlappingZones
              };

              console.log('[ZoneMapTab] Updated zoneStats with stats:', {
                prevOverlaps: prev.overlappingZones,
                prevOverlapsLength: prev.overlappingZones?.length || 0,
                updatedOverlaps: updated.overlappingZones,
                updatedOverlapsLength: updated.overlappingZones?.length || 0,
                preserved: prev.overlappingZones === updated.overlappingZones,
                overlappingZips: updated.overlappingZones?.flatMap(z => z.overlappingZips) || []
              });

              return updated;
            });

            // Show warning toast if zone is too large, but still allow dialog
            if (isZoneTooLarge(stats.totalRecalls, oemForStats)) {
              toast({
                title: 'Zone Size Warning',
                description: `This zone has ${stats.totalRecalls.toLocaleString()} ${oemForStats} recalls (Extra Large). Consider breaking it into smaller zones for better management. Ideally: ${describeRecommendedSizes(oemForStats)}.`,
                variant: 'destructive',
                duration: 8000
              });
            }
          })
          .catch(error => {
            console.error('[ZoneMapTab] Error calculating zone statistics:', error);
            // Update to show error state - don't allow zone creation with 0 recalls
            setZoneStats(prev => ({
              ...prev!,
              isLoadingStats: false,
              statsError: true
            }));
            toast({
              title: 'Stats Calculation Failed',
              description:
                'Could not calculate zone statistics. Please try again or check the browser console for details.',
              variant: 'destructive'
            });
          });
      } catch (error: any) {
        toast({
          title: 'Error Processing Shape',
          description: error.message || 'Failed to process the drawn shape',
          variant: 'destructive'
        });
        setCreating(false);
      }
    },
    [
      session?.user?.id,
      toast,
      campaignPreferences,
      selectedSnapshotDate,
      // Without selectedOem here the callback keeps whichever OEM it captured when it
      // was last built — flipping the dropdown changes none of the other deps — and a
      // Toyota zone gets counted against Ford's brands.
      selectedOem,
      checkZipCodeOverlaps,
      zones
    ]
  );

  // Handle zone creation
  const handleCreateZone = useCallback(async () => {
    if (!zoneName.trim() || !drawnGeometry || !zoneStats) {
      toast({
        title: 'Validation Error',
        description: 'Zone name is required',
        variant: 'destructive'
      });
      return;
    }

    if (!zoneRegion.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Region is required',
        variant: 'destructive'
      });
      return;
    }

    // Check for extremely large zones that might cause database issues
    if (zoneStats.zipCodes.length > 500) {
      toast({
        title: 'Zone Extremely Large',
        description: `This zone has ${zoneStats.zipCodes.length} ZIP codes. Zones this large may cause technical issues. Please consider breaking it down.`,
        variant: 'destructive'
      });
      return;
    }

    // ZIP-code overlap check is performed once by the dialog effect when
    // the dialog opens; the result lives in zoneStats.overlappingZones.
    // The Create button is disabled while that check is in flight, so
    // by the time control reaches here the data is authoritative — no
    // need to refire the RPC. (Used to be a second roundtrip per zone
    // create; removing it cuts validate_zip_code_overlaps load in half.)
    const overlappingZones = zoneStats.overlappingZones ?? [];
    if (overlappingZones.length > 0) {
      const totalOverlaps = overlappingZones.reduce(
        (sum, zone) => sum + zone.overlappingZips.length,
        0
      );

      // Keep dialog open so the user can see + fix the highlighted conflicts.
      setDialogOpen(true);

      toast({
        title: 'ZIP Code Overlap Detected',
        description: `${totalOverlaps} ZIP code${totalOverlaps === 1 ? '' : 's'} already exist${totalOverlaps === 1 ? 's' : ''} in other zones. Remove the conflicting ZIP codes (highlighted in red below) to proceed.`,
        variant: 'destructive',
        duration: 10000
      });
      return;
    }

    try {
      setCreating(true);

      // Determine shape type from geometry
      // Note: Freehand polylines are converted to polygons by normalizeGeometry
      const shapeType =
        drawnGeometry.type === 'Polygon'
          ? 'polygon'
          : drawnGeometry.type === 'MultiPolygon'
            ? 'multipolygon'
            : 'polygon'; // Default to polygon

      // Fit the drawn shape to its ZIP code boundaries BEFORE saving, so the zone is
      // written once, already ZIP-shaped. This used to be a second update after the
      // create — and the zones-list refetch kicked off by the create (which reads the
      // row before that update lands) then overwrote the cache with the drawn box
      // until the next page load. A failed fit still saves the zone, with the drawn shape.
      let zipBasedGeometry: NormalizedGeometry | null = null;
      let boundaryFitFailed = false;
      try {
        const { createZoneBoundaryFromZipCodes, bboxForGeometry } = await import(
          '../services/zipCodeBoundaries'
        );
        // The drawn shape's box lets get-zips answer in ~1 s instead of ~14 s
        zipBasedGeometry = await createZoneBoundaryFromZipCodes(zoneStats.zipCodes, {
          bbox: bboxForGeometry(drawnGeometry)
        });
      } catch (boundaryError) {
        boundaryFitFailed = true;
        console.error('[ZoneMapTab] ZIP boundary fit failed before zone create:', boundaryError);
      }

      // Geometry only. Recall counts are NOT denormalized into metadata any more: the
      // client wrote metadata.recallCount here while a background task overwrote
      // total_recalls_at_creation with a server-side figure, so the two silently diverged
      // (22 zones at last audit). Size is now always derived from total_recalls_current.
      const metadata: ZoneMetadata = {
        // drawnGeometry is always set from normalizeGeometry(), so it is a Polygon/MultiPolygon
        geometry: zipBasedGeometry ?? (drawnGeometry as NormalizedGeometry),
        shape_type: shapeType as any,
        ...(zipBasedGeometry && {
          original_geometry: drawnGeometry, // Keep original for reference
          boundary_type: 'zip_based' // Mark as ZIP-based boundary
        })
      };

      await createZone.mutateAsync({
        data: {
          zone_name: zoneName.trim(),
          ...(zoneDescription.trim() && { description: zoneDescription.trim() }),
          region: zoneRegion.trim(),
          country: 'US',
          status: 'active',
          // zoneStats.oem, not selectedOem — the baseline counts below were measured
          // against this OEM's brands, so the row has to be stamped with the same one.
          oem: zoneStats.oem,
          zip_codes: zoneStats.zipCodes,
          metadata,
          total_recalls_at_creation: zoneStats.totalRecalls,
          unique_vins_at_creation: zoneStats.uniqueVins
        },
        userId: session?.user?.id
      });

      toast({
        title: 'Zone Created',
        description: `Zone "${zoneName}" has been created successfully with ${zoneStats.zipCodes.length} ZIP codes.`
      });

      if (boundaryFitFailed) {
        toast({
          title: 'Zone Boundary Update Failed',
          description: 'Could not adjust zone to ZIP code boundaries. Using original shape.',
          variant: 'destructive'
        });
      } else if (!zipBasedGeometry) {
        toast({
          title: 'Zone Boundary Not Adjusted',
          description:
            'No ZIP code boundaries were found for this zone. ' +
            'It was saved with the shape you drew.'
        });
      }

      // Note: availableRegions is derived from the cached zones list via
      // useMemo, so a new region added by this zone create automatically
      // appears once useZoneMutations invalidates and refetches the
      // zones query. No explicit regions refetch needed here.

      // Reset state and clear the drawn shape
      setDialogOpen(false);
      setZoneName('');
      setZoneDescription('');
      setZoneRegion('');
      setDrawnGeometry(null);
      setZoneStats(null);

      // Clear the drawn shape from the map
      setShouldClearDrawnShapes(true);
      // Reset the flag after a brief delay
      setTimeout(() => setShouldClearDrawnShapes(false), 100);

      // Zone is automatically added to cache by the mutation
    } catch (error: any) {
      let errorMessage = 'Failed to create zone';
      if (error.message?.includes('409') || error.message?.includes('conflict')) {
        errorMessage =
          'Zone name already exists or conflicts with existing data. Please try a different name.';
      } else if (error.message?.includes('payload too large') || error.message?.includes('413')) {
        errorMessage =
          'Zone is too large to create. Please reduce the number of ZIP codes or draw a smaller area.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: 'Error Creating Zone',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setCreating(false);
    }
  }, [
    zoneName,
    zoneRegion,
    zoneDescription,
    drawnGeometry,
    zoneStats,
    creating,
    toast,
    checkZipCodeOverlaps,
    createZone,
    updateZone,
    session?.user?.id,
    campaignPreferences
  ]);

  // Remove ZIP code from zone during creation
  const handleRemoveZipCode = useCallback(
    async (zipCodeToRemove: string) => {
      if (!zoneStats) return;

      const updatedZipCodes = zoneStats.zipCodes.filter(zip => zip !== zipCodeToRemove);

      if (updatedZipCodes.length === 0) {
        toast({
          title: 'Cannot Remove ZIP Code',
          description: 'A zone must contain at least one ZIP code.',
          variant: 'destructive'
        });
        return;
      }

      try {
        // Check for overlaps with updated ZIP codes
        const overlappingZones = checkZipCodeOverlaps(updatedZipCodes);

        // Recalculate against the same OEM the zone was drawn under, not whatever
        // the map dropdown holds now, so removing a ZIP can't silently reprice the
        // zone against a different brand set.
        const stats = await calculateZoneStatistics(
          updatedZipCodes,
          session?.user?.id,
          campaignPreferences as any,
          selectedSnapshotDate,
          zoneStats.oem
        );

        setZoneStats({
          zipCodes: updatedZipCodes,
          oem: zoneStats.oem,
          totalRecalls: stats.totalRecalls,
          uniqueVins: stats.uniqueVins,
          overlappingZones
        });

        toast({
          title: 'ZIP Code Removed',
          description: `ZIP code ${zipCodeToRemove} has been removed from the zone.`
        });
      } catch (error: any) {
        toast({
          title: 'Error Updating Zone',
          description: error.message || 'Failed to update zone statistics',
          variant: 'destructive'
        });
      }
    },
    [zoneStats, session?.user?.id, toast, campaignPreferences, selectedSnapshotDate]
  );

  // Cancel zone creation
  const handleCancel = useCallback(() => {
    setDialogOpen(false);
    setZoneName('');
    setZoneDescription('');
    setZoneRegion('');
    setDrawnGeometry(null);
    setZoneStats(null);
  }, []);

  // Handle zone deletion
  const handleDeleteZone = useCallback(async (zone: Zone) => {
    setZoneToDelete(zone);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDeleteZone = useCallback(async () => {
    if (!zoneToDelete) return;

    try {
      setCreating(true);
      await deleteZone.mutateAsync({ id: zoneToDelete.id, hard: true, userId: session?.user?.id }); // Hard delete - completely removes from database

      toast({
        title: 'Zone Deleted',
        description: `Zone "${zoneToDelete.zone_name}" has been deleted.`
      });

      // Zone is automatically removed from cache by the mutation
      setDeleteDialogOpen(false);
      setZoneToDelete(null);
    } catch (error: any) {
      toast({
        title: 'Error Deleting Zone',
        description: error.message || 'Failed to delete zone',
        variant: 'destructive'
      });
    } finally {
      setCreating(false);
    }
  }, [zoneToDelete, toast, deleteZone, session?.user?.id]);

  // Remove ZIP code from existing zone
  const handleRemoveZipCodeFromExistingZone = useCallback(
    async (zone: Zone, zipCodeToRemove: string) => {
      const currentZipCodes = zone.zip_codes || [];
      const updatedZipCodes = currentZipCodes.filter(zip => zip !== zipCodeToRemove);

      if (updatedZipCodes.length === 0) {
        toast({
          title: 'Cannot Remove ZIP Code',
          description: 'A zone must contain at least one ZIP code. Delete the zone instead.',
          variant: 'destructive'
        });
        return;
      }

      try {
        setUpdatingZoneBoundaries(true);

        // Check if the updated ZIP codes would cause overlaps (shouldn't happen since we're removing, but good to check)
        const overlappingZones = checkZipCodeOverlaps(updatedZipCodes, zone.id);
        if (overlappingZones.length > 0) {
          toast({
            title: 'Cannot Update Zone',
            description: 'This change would create ZIP code overlaps with existing zones.',
            variant: 'destructive'
          });
          return;
        }

        // Recalculate statistics with the updated ZIP codes
        // Pass zone.oem to filter recalls by the zone's OEM brands
        const stats = await calculateZoneStatistics(
          updatedZipCodes,
          session?.user?.id,
          campaignPreferences as any,
          selectedSnapshotDate,
          zone.oem
        );

        // Create new geometry from updated ZIP codes. A failed fetch must not block the
        // ZIP removal itself — the zone just keeps its current outline.
        const { createZoneBoundaryFromZipCodes, bboxForGeometry } = await import(
          '../services/zipCodeBoundaries'
        );
        const currentMetadata = zone.metadata || {};
        let zipBasedGeometry: Awaited<ReturnType<typeof createZoneBoundaryFromZipCodes>> = null;
        try {
          zipBasedGeometry = await createZoneBoundaryFromZipCodes(updatedZipCodes, {
            bbox: bboxForGeometry(currentMetadata['geometry'])
          });
        } catch (boundaryError) {
          console.error('[ZoneMapTab] ZIP boundary refit failed after ZIP removal:', boundaryError);
        }

        // Update zone geometry only — recall counts are no longer denormalized into
        // metadata. The server recomputes them from the new ZIP set on the next refresh.
        // Without a new geometry, metadata is left out entirely: the list's
        // metadata.geometry is the simplified copy, and writing it back would
        // permanently replace the full-fidelity polygon.
        await updateZone.mutateAsync({
          id: zone.id,
          data: {
            zip_codes: updatedZipCodes,
            ...(zipBasedGeometry && {
              metadata: {
                ...currentMetadata,
                geometry: zipBasedGeometry,
                boundary_type: 'zip_based'
              } as any
            })
          },
          userId: session?.user?.id
        });

        const removedFrom = `ZIP code ${zipCodeToRemove} has been removed from "${zone.zone_name}"`;
        toast({
          title: 'ZIP Code Removed',
          description: zipBasedGeometry
            ? `${removedFrom}. Zone boundaries have been updated.`
            : `${removedFrom}, but the zone outline could not be redrawn from ZIP boundaries.`
        });

        // Zone is automatically updated in cache by the mutation
      } catch (error: any) {
        toast({
          title: 'Error Removing ZIP Code',
          description: error.message || 'Failed to remove ZIP code from zone',
          variant: 'destructive'
        });
      } finally {
        setUpdatingZoneBoundaries(false);
      }
    },
    [session?.user?.id, toast, updateZone, checkZipCodeOverlaps, campaignPreferences]
  );

  // Check for ZIP code overlaps with existing zones

  if (recallsError) {
    return (
      <Card>
        <CardContent className='pt-6'>
          <div className='text-center text-destructive'>
            <p>Error loading recalls: {recallsError}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isLoading = loadingZones || updatingZoneBoundaries;
  // Top-tier zones get the "too large, consider splitting" treatment. The cutoff is
  // per-OEM, and zoneStats.oem is the OEM snapshotted when the shape was drawn — not
  // selectedOem, which can change while the dialog is open.
  const isZoneOversized = zoneStats
    ? isZoneTooLarge(zoneStats.totalRecalls, zoneStats.oem)
    : false;
  const filteredZonesForSelect = zones
    .filter(zone => zone.oem === selectedOem)
    .filter(zone => regionFilter === 'all' || zone.region === regionFilter)
    .sort((a, b) => (a.zone_name || '').localeCompare(b.zone_name || ''));

  return (
    <div
      className={`h-full w-full relative ${isFullscreen ? 'zone-map-fullscreen' : ''}`}
      style={
        isFullscreen
          ? {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'var(--background)'
            }
          : undefined
      }
    >
      {/* Map fills the entire viewport — no chrome above it. All filters
          and toggles live in a floating overlay (top-left). The Leaflet
          drawing tools render inside the map itself in the top-left
          corner of the canvas. */}
      {loadingZonesError && (
        <div className='absolute top-3 left-1/2 -translate-x-1/2 z-[1001] bg-destructive/10 border border-destructive text-destructive rounded-lg px-4 py-2 text-sm flex items-center gap-2 pointer-events-auto'>
          <AlertTriangle className='h-4 w-4 flex-shrink-0' />
          <span>
            Failed to load zones:{' '}
            {loadingZonesError instanceof Error ? loadingZonesError.message : 'Unknown error'}
          </span>
          <button onClick={() => refetchZones()} className='underline ml-1'>
            Retry
          </button>
        </div>
      )}
      <div className='absolute inset-0'>
        {/* recallCountsNow is passed at the top level rather than inside
            presentationConfig — Design mode needs it too. Without it, polygons size
            off the denormalized total_recalls_current column, which the nightly
            refresh job hasn't written yet for a zone created today, so a new zone
            renders as "Small (0)" until the next morning. */}
        <ZoneDrawingMapOptimized
          dealerLocations={
            mapMode === 'presentation' ? densityDealerLocations : filteredDealerLocations
          }
          zones={zonesInViewport}
          onShapeDrawn={handleShapeDrawn}
          onMapBoundsChange={handleMapBoundsChange}
          selectedZoneId={selectedZoneId}
          onZoneDelete={zoneId => {
            const zone = zones.find(z => z.id === zoneId);
            if (zone) handleDeleteZone(zone);
          }}
          onRemoveZipCode={handleRemoveZipCodeFromExistingZone}
          loading={recallsLoading}
          clearDrawnShapes={shouldClearDrawnShapes}
          showBubbles={mapMode === 'presentation' ? false : showBubbles}
          uniformColor={uniformColor}
          drawingEnabled={mapMode === 'design'}
          {...(recallCountsNow && { recallCountsNow })}
          {...(crawlDatesNow && { crawlDatesNow })}
          {...(presentationConfig && { presentationMode: presentationConfig })}
        />
      </div>

      {/* Floating filter overlay — primary filter scope + display toggles
          + view switch. Modeled on Mapbox/Google-Maps-Studio overlays:
          the map keeps full bleed and controls float on top. */}
      <div
        data-export-ignore='true'
        className='absolute top-3 left-3 z-[1000] bg-card/95 backdrop-blur-sm shadow-lg border rounded-lg p-3 w-[300px] space-y-3'
      >
        {/* Mode toggle: Design = drawing surface; Presentation = customer-
            shareable view with PNG / ZIP export. */}
        <Tabs
          value={mapMode}
          onValueChange={value => setMapMode(value as 'design' | 'presentation')}
        >
          <TabsList className='grid grid-cols-2 w-full h-8'>
            <TabsTrigger value='design' className='text-xs gap-1.5'>
              <PencilRuler className='h-3.5 w-3.5' />
              Design
            </TabsTrigger>
            <TabsTrigger value='presentation' className='text-xs gap-1.5'>
              <Eye className='h-3.5 w-3.5' />
              Presentation
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Header: at-a-glance counts + loading spinner */}
        <div className='flex items-center justify-between text-xs text-muted-foreground'>
          <span className='flex items-center gap-1.5'>
            <Map className='h-3.5 w-3.5' />
            {mapMode === 'presentation'
              ? `${zonesForExport.length} zone${zonesForExport.length === 1 ? '' : 's'} · ready to share`
              : recallsLoading
                ? 'Loading…'
                : `${totalDealers.toLocaleString()} dealers · ${totalRecalls.toLocaleString()} recalls`}
          </span>
          {isLoading ? (
            <Loader2 className='h-3.5 w-3.5 animate-spin text-primary' />
          ) : (
            <span className='tabular-nums'>{zones.length} zones</span>
          )}
        </div>

        {/* Filters */}
        <div className='space-y-2'>
          <div className='space-y-1'>
            <label className='text-[11px] font-medium text-muted-foreground'>OEM</label>
            <Select value={selectedOem} onValueChange={setSelectedOem}>
              <SelectTrigger className='w-full h-9'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className='z-[1100]'>
                {KNOWN_OEMS.filter(oem => oem !== 'Other').map(oem => (
                  <SelectItem key={oem} value={oem}>
                    {oem}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-1'>
            <label className='text-[11px] font-medium text-muted-foreground'>Region</label>
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className='w-full h-9'>
                <SelectValue placeholder='All Regions' />
              </SelectTrigger>
              <SelectContent className='z-[1100]'>
                <SelectItem value='all'>All Regions</SelectItem>
                {availableRegions.map(region => (
                  <SelectItem key={region} value={region}>
                    {region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-1'>
            <label className='text-[11px] font-medium text-muted-foreground'>Zones</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-full h-9 justify-between font-normal'
                  type='button'
                >
                  <span className='truncate'>
                    {zoneFilter.length === 0
                      ? 'All zones'
                      : `${zoneFilter.length} zone${zoneFilter.length !== 1 ? 's' : ''} selected`}
                  </span>
                  <ChevronDown className='h-3.5 w-3.5 opacity-50 flex-shrink-0' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-[300px] p-0 z-[1100]' align='start'>
                <div className='flex items-center justify-between p-2 border-b'>
                  <span className='text-xs font-medium'>{filteredZonesForSelect.length} zones</span>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-7 text-xs'
                    onClick={() => setZoneFilter([])}
                    disabled={zoneFilter.length === 0}
                  >
                    Clear
                  </Button>
                </div>
                <div className='max-h-[260px] overflow-y-auto p-2 space-y-1'>
                  {filteredZonesForSelect.map(zone => (
                    <label
                      key={zone.id}
                      className='flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer'
                    >
                      <Checkbox
                        checked={zoneFilter.includes(zone.id)}
                        onCheckedChange={checked => {
                          setZoneFilter(prev =>
                            checked ? [...prev, zone.id] : prev.filter(id => id !== zone.id)
                          );
                        }}
                      />
                      {mapMode === 'presentation' && tenantByZone && (
                        <span
                          className='inline-block h-2 w-2 rounded-full flex-shrink-0'
                          style={{
                            background: tenantByZone[zone.id]
                              ? ASSIGNED_ZONE_COLOR.stroke
                              : AVAILABLE_ZONE_COLOR.fill
                          }}
                          title={
                            tenantByZone[zone.id]
                              ? `Assigned to ${tenantByZone[zone.id]}`
                              : 'Available'
                          }
                        >
                          <span className='sr-only'>
                            {tenantByZone[zone.id] ? 'Assigned' : 'Available'}
                          </span>
                        </span>
                      )}
                      <span className='text-sm truncate'>{zone.zone_name}</span>
                      {zone.region && (
                        <span className='text-xs text-muted-foreground ml-auto flex-shrink-0'>
                          {zone.region}
                        </span>
                      )}
                    </label>
                  ))}
                  {filteredZonesForSelect.length === 0 && (
                    <p className='text-xs text-muted-foreground text-center py-4'>
                      No zones match the current OEM / Region.
                    </p>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Display toggles + view switch.
            Design mode: bubble + uniform-color toggles for the drawing surface.
            Presentation mode: KPI line toggles + PNG / ZIP download. */}
        <div className='border-t pt-2 space-y-2'>
          {mapMode === 'design' ? (
            <>
              <label className='flex items-center justify-between gap-2 cursor-pointer'>
                <span className='text-xs flex items-center gap-1.5'>
                  <CircleDot className='h-3.5 w-3.5 text-muted-foreground' />
                  Recall bubbles
                </span>
                <Switch checked={showBubbles} onCheckedChange={setShowBubbles} />
              </label>
              <label className='flex items-center justify-between gap-2 cursor-pointer'>
                <span className='text-xs flex items-center gap-1.5'>
                  <Paintbrush className='h-3.5 w-3.5 text-muted-foreground' />
                  Uniform zone color
                </span>
                <Switch checked={uniformColor} onCheckedChange={setUniformColor} />
              </label>
            </>
          ) : (
            <>
              <label className='flex items-center justify-between gap-2 cursor-pointer'>
                <span className='text-xs flex items-center gap-1.5'>
                  <Building2 className='h-3.5 w-3.5 text-muted-foreground' />
                  Tenant names
                </span>
                <Switch checked={showTenantNames} onCheckedChange={setShowTenantNames} />
              </label>
              <label className='flex items-center justify-between gap-2 cursor-pointer'>
                <span className='text-xs flex items-center gap-1.5'>
                  <CircleDot className='h-3.5 w-3.5 text-muted-foreground' />
                  Current recall count
                </span>
                <Switch checked={showNowRecalls} onCheckedChange={setShowNowRecalls} />
              </label>
              <label className='flex items-center justify-between gap-2 cursor-pointer'>
                <span className='text-xs flex items-center gap-1.5'>
                  <CircleDot className='h-3.5 w-3.5 text-muted-foreground' />
                  Recall count at zone creation
                </span>
                <Switch checked={showCreationRecalls} onCheckedChange={setShowCreationRecalls} />
              </label>
              <label
                className='flex items-center justify-between gap-2 cursor-pointer'
                title='One dot per dealer inside the zones shown; size scales with recall count'
              >
                <span className='text-xs flex items-center gap-1.5'>
                  <CircleDot className='h-3.5 w-3.5 text-muted-foreground' />
                  Recall density
                </span>
                <Switch checked={showRecallDensity} onCheckedChange={setShowRecallDensity} />
              </label>
              <div className='grid grid-cols-2 gap-2 pt-1'>
                <Button
                  variant='outline'
                  size='sm'
                  className='h-8 text-xs gap-1.5'
                  onClick={handleDownloadPng}
                  disabled={isExportingPng || zonesForExport.length === 0}
                  title={
                    zonesForExport.length === 0
                      ? 'Select zones or zoom to a region with zones first'
                      : 'Download a shareable PNG of the selected zones'
                  }
                >
                  {isExportingPng ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Download className='h-3.5 w-3.5' />
                  )}
                  PNG
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  className='h-8 text-xs gap-1.5'
                  onClick={handleDownloadZips}
                  disabled={isExportingZips || zonesForExport.length === 0}
                  title={
                    zonesForExport.length === 0
                      ? 'Select zones or zoom to a region with zones first'
                      : 'Download an Excel workbook of the selected zones’ ZIP codes'
                  }
                >
                  {isExportingZips ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <FileSpreadsheet className='h-3.5 w-3.5' />
                  )}
                  ZIPs
                </Button>
              </div>
            </>
          )}

          {onViewChange && (
            <div className='flex items-center justify-between pt-1'>
              <span className='text-xs text-muted-foreground'>View</span>
              <div className='flex items-center bg-muted p-1 rounded-md h-8'>
                <Button
                  variant={view === 'list' ? 'secondary' : 'ghost'}
                  size='sm'
                  className='h-6 w-6 p-0'
                  onClick={() => onViewChange('list')}
                  title='List view'
                  aria-label='List view'
                >
                  <List className='h-3.5 w-3.5' />
                </Button>
                <Button
                  variant={view === 'map' ? 'secondary' : 'ghost'}
                  size='sm'
                  className='h-6 w-6 p-0'
                  onClick={() => onViewChange('map')}
                  title='Map view'
                  aria-label='Map view'
                >
                  <Map className='h-3.5 w-3.5' />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Read-only status line — only when there's something to show */}
        {(activeCampaignFilters > 0 || excludedDealerCount > 0) && (
          <div className='border-t pt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground'>
            <Filter className='h-3 w-3 flex-shrink-0' />
            <span className='truncate'>
              {activeCampaignFilters > 0 && (
                <>
                  {activeCampaignFilters} campaign filter{activeCampaignFilters !== 1 ? 's' : ''}
                </>
              )}
              {activeCampaignFilters > 0 && excludedDealerCount > 0 && ' · '}
              {excludedDealerCount > 0 && (
                <>
                  {excludedDealerCount} excluded dealer{excludedDealerCount !== 1 ? 's' : ''}
                </>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Fullscreen toggle button — bottom-right corner so it never collides
          with Leaflet's zoom controls (top-right) or the floating filter
          overlay (top-left). In fullscreen mode the button gets a visible
          "Exit fullscreen" label so users always know how to get back out.
          Esc key is also wired up as a keyboard escape. */}
      <button
        onClick={() => setIsFullscreen(!isFullscreen)}
        className={
          isFullscreen
            ? 'absolute bottom-3 right-3 z-[1000] bg-foreground text-background rounded-lg shadow-lg border border-border px-3 py-2 hover:opacity-90 transition-colors flex items-center gap-2'
            : 'absolute bottom-3 right-3 z-[1000] bg-card rounded-lg shadow-lg border border-border p-2 hover:bg-accent transition-colors'
        }
        title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen'}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isFullscreen ? (
          <>
            <Minimize2 className='h-4 w-4' />
            <span className='text-sm font-medium'>Exit fullscreen</span>
          </>
        ) : (
          <Maximize2 className='h-4 w-4 text-foreground' />
        )}
      </button>

      {/* Old top-row + Collapsible filter panel removed (244+ lines).
          All filters/toggles/counts now live in the floating overlay above. */}

      {/* Add animation styles */}
      <style>{styles}</style>
      {/* Fullscreen styles */}
      <style>{`
        .zone-map-fullscreen {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          z-index: 99999 !important;
          background: var(--background) !important;
          width: 100vw !important;
          height: 100vh !important;
          isolation: isolate !important;
        }
        .zone-map-fullscreen-container {
          height: 100vh !important;
          width: 100vw !important;
        }
        .zone-map-fullscreen > div:first-child {
          background-color: var(--background) !important;
          z-index: 99999 !important;
        }
        /* Hide sidebar and other layout elements when fullscreen */
        body:has(.zone-map-fullscreen) .sidebar,
        body:has(.zone-map-fullscreen) [class*="sidebar"],
        body:has(.zone-map-fullscreen) header,
        body:has(.zone-map-fullscreen) [class*="StandaloneSidebar"] {
          display: none !important;
        }
        /* Ensure dialog appears above fullscreen container */
        body:has(.zone-map-fullscreen) [data-radix-portal] {
          z-index: 100000 !important;
        }
        body:has(.zone-map-fullscreen) [data-radix-dialog-overlay] {
          z-index: 100000 !important;
        }
        body:has(.zone-map-fullscreen) [data-radix-dialog-content] {
          z-index: 100001 !important;
        }
        /* Ensure Select dropdowns appear above fullscreen container */
        body:has(.zone-map-fullscreen) [data-radix-popper-content-wrapper] {
          z-index: 100000 !important;
        }
      `}</style>

      {/* Zone Creation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='max-w-lg z-[100000]'>
          <DialogHeader>
            <DialogTitle className='text-xl'>Create New Zone</DialogTitle>
            <DialogDescription className='text-base'>
              Define your zone details. This area includes{' '}
              <strong>{zoneStats?.zipCodes.length || 0} ZIP codes</strong> with{' '}
              <strong>
                {zoneStats?.totalRecalls.toLocaleString() || 0} {zoneStats?.oem} recalls
              </strong>
              .
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-6 py-4'>
            {/* Zone Statistics - More prominent */}
            {zoneStats && (
              <>
                {/* Warning for Extra Large zones */}
                {isZoneOversized && (
                  <div className='rounded-lg border-2 border-destructive/20 bg-destructive/5 p-4'>
                    <div className='flex items-center gap-2 mb-2'>
                      <span className='text-destructive text-lg'>⚠️</span>
                      <span className='font-semibold text-destructive'>Zone Too Large</span>
                    </div>
                    <p className='text-sm text-muted-foreground'>
                      This zone has {zoneStats.totalRecalls.toLocaleString()} {zoneStats.oem}{' '}
                      recalls (Extra Large). Consider breaking it into smaller zones for better
                      management.
                    </p>
                    <p className='text-xs text-muted-foreground mt-2'>
                      Recommended: {describeRecommendedSizes(zoneStats.oem)}
                    </p>
                  </div>
                )}

                {/* ZIP Code Overlap Warning - Always show if overlaps exist */}
                {(() => {
                  console.log('[ZoneMapTab] Checking overlap warning - zoneStats:', {
                    hasZoneStats: !!zoneStats,
                    hasOverlappingZones: !!zoneStats?.overlappingZones,
                    overlappingZones: zoneStats?.overlappingZones,
                    isArray: Array.isArray(zoneStats?.overlappingZones),
                    length: zoneStats?.overlappingZones?.length || 0,
                    type: typeof zoneStats?.overlappingZones
                  });

                  const hasOverlaps =
                    zoneStats?.overlappingZones &&
                    Array.isArray(zoneStats.overlappingZones) &&
                    zoneStats.overlappingZones.length > 0;

                  console.log('[ZoneMapTab] Overlap warning check result:', hasOverlaps);

                  if (hasOverlaps) {
                    console.log(
                      '[ZoneMapTab] RENDERING overlap warning section with',
                      zoneStats.overlappingZones.length,
                      'overlaps'
                    );
                  } else {
                    console.log('[ZoneMapTab] NOT rendering overlap warning - condition failed');
                  }

                  return hasOverlaps ? (
                    <div className='rounded-lg border-2 border-destructive/20 bg-destructive/5 p-4'>
                      <div className='flex items-center gap-2 mb-2'>
                        <span className='text-destructive text-lg'>🚫</span>
                        <span className='font-semibold text-destructive'>
                          ZIP Code Overlap Detected (
                          {zoneStats.overlappingZones.reduce(
                            (sum, z) => sum + z.overlappingZips.length,
                            0
                          )}{' '}
                          conflicts)
                        </span>
                      </div>
                      <p className='text-sm text-muted-foreground mb-3'>
                        Some ZIP codes already exist in other zones. Each ZIP code can only belong
                        to one zone.{' '}
                        <strong>
                          Remove the conflicting ZIP codes (highlighted in red below) to proceed
                          with zone creation.
                        </strong>
                      </p>
                      <div className='space-y-2'>
                        {zoneStats.overlappingZones.map((overlap, index) => (
                          <div key={index} className='bg-destructive/10 rounded p-2'>
                            <div className='text-sm font-medium text-destructive mb-1'>
                              Zone: {overlap.zoneName}
                            </div>
                            <div className='text-xs text-muted-foreground'>
                              Conflicting ZIP codes: {overlap.overlappingZips.join(', ')}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className='mt-3 flex items-center justify-between'>
                        <p className='text-xs text-destructive font-medium'>
                          Please adjust your selection to remove overlapping areas.
                        </p>
                        <Button
                          size='sm'
                          variant='destructive'
                          onClick={() => {
                            // Remove all conflicting zip codes
                            const conflictingZips = new Set(
                              zoneStats.overlappingZones?.flatMap(z => z.overlappingZips) || []
                            );
                            const filteredZips = zoneStats.zipCodes.filter(
                              zip => !conflictingZips.has(zip)
                            );
                            if (filteredZips.length === 0) {
                              toast({
                                title: 'Cannot Remove All ZIP Codes',
                                description:
                                  'Removing all conflicting ZIP codes would leave the zone empty.',
                                variant: 'destructive'
                              });
                              return;
                            }
                            // Update zone stats without conflicting zips
                            setZoneStats(prev => ({
                              ...prev!,
                              zipCodes: filteredZips,
                              overlappingZones: [],
                              isLoadingStats: true
                            }));
                            calculateZoneStatistics(
                              filteredZips,
                              session?.user?.id,
                              campaignPreferences,
                              selectedSnapshotDate,
                              zoneStats.oem
                            )
                              .then(stats => {
                                setZoneStats(prev => ({
                                  ...prev!,
                                  totalRecalls: stats.totalRecalls,
                                  uniqueVins: stats.uniqueVins,
                                  isLoadingStats: false
                                }));
                                toast({
                                  title: 'Conflicting ZIP Codes Removed',
                                  description: `Removed ${conflictingZips.size} conflicting ZIP code(s).`
                                });
                              })
                              .catch(error => {
                                console.error('[ZoneMapTab] Error recalculating stats:', error);
                                setZoneStats(prev => ({
                                  ...prev!,
                                  isLoadingStats: false
                                }));
                              });
                          }}
                          disabled={creating}
                        >
                          Quick Remove Conflicting
                        </Button>
                      </div>
                    </div>
                  ) : null;
                })()}

                <div
                  className={`rounded-lg border-2 p-4 ${isZoneOversized ? 'border-destructive/20 bg-destructive/5' : 'border-primary/20 bg-primary/5'}`}
                >
                  <div className='grid grid-cols-2 gap-4 text-center'>
                    <div>
                      <div className='text-2xl font-bold text-primary'>
                        {zoneStats.zipCodes.length}
                      </div>
                      <div className='text-xs text-muted-foreground'>ZIP Codes</div>
                    </div>
                    <div>
                      {zoneStats.isLoadingStats ? (
                        <div className='flex items-center justify-center gap-2'>
                          <Loader2 className='h-5 w-5 animate-spin text-primary' />
                          <span className='text-sm text-muted-foreground'>Calculating...</span>
                        </div>
                      ) : zoneStats.statsError ? (
                        <div className='flex flex-col items-center justify-center gap-1'>
                          <div className='text-destructive text-sm font-medium'>Failed to load</div>
                          <div className='text-xs text-muted-foreground'>
                            Check console for details
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            className={`text-2xl font-bold ${isZoneOversized ? 'text-destructive' : 'text-primary'}`}
                          >
                            {zoneStats.totalRecalls.toLocaleString()}
                          </div>
                          <div className='text-xs text-muted-foreground'>
                            {zoneStats.oem} Recalls
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {!zoneStats.isLoadingStats && !zoneStats.statsError && (
                    <div className='mt-3 text-center'>
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                          isZoneOversized
                            ? 'bg-destructive/10 text-destructive'
                            : 'bg-primary/10 text-primary'
                        }`}
                      >
                        {getZoneSizeLabel(zoneStats.totalRecalls, zoneStats.oem)} Zone
                      </span>
                      {/* A zone being drawn has no identity and no tenant yet, so this
                          preview necessarily counts every matching recall in the area.
                          Once the zone is created and assigned, its badge counts only that
                          dealer's own crawl data and so usually reads lower. Said plainly
                          here because the drop otherwise looks like the zone shrank. */}
                      <p className='mt-1.5 text-[11px] text-muted-foreground'>
                        Counts all {zoneStats.oem} recalls in this area. Once the zone is assigned
                        to a dealer it will count only that dealer&apos;s, which is usually fewer.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* OEM indicator - shows which OEM this zone will be created for.
                Reads zoneStats.oem (the OEM the counts above were measured under),
                not selectedOem — this badge used to say "Toyota" over a Ford count. */}
            <div className='flex items-center gap-2 py-2 px-3 bg-muted rounded-md'>
              <span className='text-sm text-muted-foreground'>Creating zone for:</span>
              <Badge variant='secondary' className='font-medium'>
                {zoneStats?.oem ?? selectedOem}
              </Badge>
            </div>

            {/* Form fields - simplified */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='zone-name'>Zone Name *</Label>
                <Input
                  id='zone-name'
                  value={zoneName}
                  onChange={e => setZoneName(e.target.value)}
                  placeholder='e.g., Tampa Bay'
                  disabled={creating}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='zone-region'>Region *</Label>
                <Input
                  id='zone-region'
                  value={zoneRegion}
                  onChange={e => setZoneRegion(e.target.value)}
                  placeholder='e.g., Southeast'
                  disabled={creating}
                />
                <p className='text-xs text-muted-foreground'>
                  Multiple zones can share the same region
                </p>
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='zone-description'>Description (Optional)</Label>
              <Input
                id='zone-description'
                value={zoneDescription}
                onChange={e => setZoneDescription(e.target.value)}
                placeholder='e.g., High priority area for recall campaigns'
                disabled={creating}
              />
            </div>

            {/* Included ZIP Codes */}
            {zoneStats && zoneStats.zipCodes && zoneStats.zipCodes.length > 0 && (
              <div className='space-y-2 border-t pt-4'>
                <Label className='text-sm font-medium'>
                  Included ZIP Codes ({zoneStats.zipCodes.length})
                  <span className='text-xs text-muted-foreground ml-2'>Click × to remove</span>
                </Label>
                <div className='max-h-32 overflow-y-auto rounded-md border bg-muted/30 p-3'>
                  <div className='flex flex-wrap gap-2'>
                    {(() => {
                      // Extract all conflicting ZIP codes from overlapping zones
                      const overlappingZones = zoneStats.overlappingZones || [];
                      const conflictingZips = new Set<string>();

                      console.log(
                        '[ZoneMapTab] ZIP Codes Section - zoneStats.overlappingZones:',
                        zoneStats.overlappingZones
                      );
                      console.log(
                        '[ZoneMapTab] ZIP Codes Section - overlappingZones array:',
                        overlappingZones
                      );

                      overlappingZones.forEach(overlap => {
                        console.log('[ZoneMapTab] Processing overlap:', overlap);
                        if (Array.isArray(overlap.overlappingZips)) {
                          overlap.overlappingZips.forEach(zip => {
                            console.log('[ZoneMapTab] Adding conflicting ZIP:', zip);
                            conflictingZips.add(zip);
                          });
                        }
                      });

                      // Debug logging
                      console.log('[ZoneMapTab] Rendering ZIP codes section:', {
                        zoneStatsOverlappingZones: zoneStats.overlappingZones,
                        overlappingZonesCount: overlappingZones.length,
                        conflictingZipsCount: conflictingZips.size,
                        conflictingZips: Array.from(conflictingZips),
                        allZips: zoneStats.zipCodes.length,
                        allZipsList: zoneStats.zipCodes.slice(0, 10)
                      });

                      // Sort ZIP codes: conflicting ones first, then non-conflicting
                      const sortedZipCodes = [...zoneStats.zipCodes].sort((a, b) => {
                        const aIsConflicting = conflictingZips.has(a);
                        const bIsConflicting = conflictingZips.has(b);
                        if (aIsConflicting && !bIsConflicting) return -1; // a comes first
                        if (!aIsConflicting && bIsConflicting) return 1; // b comes first
                        return a.localeCompare(b); // Both same type, sort alphabetically
                      });

                      return sortedZipCodes.map(zipCode => {
                        // Check if this ZIP code is conflicting
                        const isConflicting = conflictingZips.has(zipCode);

                        // Debug log for conflicting ZIPs
                        if (isConflicting) {
                          console.log(
                            '[ZoneMapTab] ⚠️ CONFLICTING ZIP CODE FOUND - Rendering in RED:',
                            zipCode
                          );
                        }

                        return (
                          <div key={zipCode} className='relative group'>
                            <Badge
                              variant={isConflicting ? 'destructive' : 'secondary'}
                              className={cn(
                                'font-mono text-xs pr-6',
                                isConflicting && [
                                  'zip-bubble-animation',
                                  '!bg-red-600',
                                  '!text-white',
                                  '!border-2',
                                  '!border-red-800',
                                  'border-solid',
                                  'shadow-lg',
                                  'ring-2',
                                  'ring-red-400'
                                ]
                              )}
                              title={
                                isConflicting
                                  ? 'This ZIP code conflicts with another zone - click × to remove'
                                  : undefined
                              }
                            >
                              {zipCode}
                            </Badge>
                            <button
                              onClick={() => handleRemoveZipCode(zipCode)}
                              disabled={creating || zoneStats.zipCodes.length <= 1}
                              className={`absolute -top-1 -right-1 w-4 h-4 rounded-full ${
                                isConflicting ? 'bg-destructive/80' : 'bg-destructive'
                              } text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${
                                zoneStats.zipCodes.length <= 1
                                  ? 'cursor-not-allowed opacity-30'
                                  : 'hover:bg-destructive/80'
                              }`}
                              title={
                                zoneStats.zipCodes.length <= 1
                                  ? 'Cannot remove the last ZIP code'
                                  : `Remove ${zipCode}`
                              }
                            >
                              <X className='h-2.5 w-2.5' />
                            </button>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className='gap-3'>
            <Button variant='outline' onClick={handleCancel} disabled={creating}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateZone}
              disabled={
                creating ||
                isCheckingOverlaps ||
                !zoneName.trim() ||
                !zoneRegion.trim() ||
                zoneStats?.isLoadingStats ||
                zoneStats?.statsError
              }
              className='min-w-24'
            >
              {creating ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Creating...
                </>
              ) : isCheckingOverlaps ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Checking…
                </>
              ) : (
                'Create Zone'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently Delete Zone</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete the zone &quot;{zoneToDelete?.zone_name}
              &quot;? This will completely remove the zone, its boundaries, and all associated data.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDeleteDialogOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button variant='destructive' onClick={confirmDeleteZone} disabled={creating}>
              {creating && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
              Delete Zone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
